/**
 * @file Worker.jsx
 * @description Worker component for displaying and managing worker table.
 * @component Worker
 * @exports Worker - Main component for displaying and managing worker table.
 */

import { useState, useEffect, useMemo } from "react";
import { TextField, Alert } from "@mui/material";
import { getComparator, handleSort } from "../../../hooks/useTable.js";
import { getUsers } from "../../../services/adminModule/users/userServices.js";
import { getActiveReasons, handleOfflineStatus } from "../../../services/agentModule/status/statusServices.js";
import { getIvrWorker, deleteIvrWorker, disableIvrWorker } from "../../../services/ivrModule/workerServices.js";
import { getIvrAssignment, completeIvrAssignment, closeIvrAssignment } from "../../../services/ivrModule/assignmentServices.js";
import { USER_ERRORS } from "../../../common/constants/adminModule/userConstants.js";
import { STATUS_ERRORS } from "../../../common/constants/agentModule/statusConstants.js";
import { ASSIGNMENT_ERRORS } from "../../../common/constants/ivrModule/assignmentConstants.js";
import { AUTO_COMPLETE, VARIANTS, STATUS, SORT_ORDERS, SIZES } from "../../../common/constants/commonConstants.js";
import { WORKER_STRINGS, WORKER_KEYS, WORKER_ERRORS, WORKER_TABLE_COLUMNS } from "../../../common/constants/ivrModule/workerConstants.js";
import WorkerTable from "./WorkerTable.jsx";
import DeleteWorkerDialog from "./DeleteWorkerDialog.jsx";
import DisableWorkerDialog from "./DisableWorkerDialog.jsx";
import ProgressOverlay from "../../../common/components/ProgressOverlay.jsx";
import { useAgentStatus } from "../../../providers/AgentStatusContext.jsx";

const Worker = () => {
    const [page, setPage] = useState(0);
    const disableReason = "End of Shift";
    const [users, setUsers] = useState([]);
    const [ivrWorkers, setIvrWorkers] = useState([]);
    const { triggerStatusRefresh } = useAgentStatus();
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [userReasons, setUserReasons] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDisabling, setIsDisabling] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [loadingWorker, setLoadingWorker] = useState(true);
    const [successMessage, setSuccessMessage] = useState("");
    const [ivrAssignments, setIvrAssignments] = useState([]);
    const [order, setOrder] = useState(SORT_ORDERS.ASCENDING);
    const [loadingReasons, setLoadingReasons] = useState(true);
    const [selectedWorker, setSelectedWorker] = useState(null);
    const [searchIvrWorker, setSearchIvrWorker] = useState("");
    const [orderBy, setOrderBy] = useState(WORKER_KEYS.WORKER_NAME);
    const [loadingAssignment, setLoadingAssignment] = useState(true);
    const [isDeleteWorkerModalOpen, setIsDeleteWorkerModalOpen] = useState(false);
    const [isDisableWorkerModalOpen, setIsDisableWorkerModalOpen] = useState(false);

    useEffect(() => { fetchWorker(); fetchAssignment(); loadUsers(); fetchReasons();}, []);

    const fetchWorker = async () => {
        try {
            setLoadingWorker(true);
            setIvrWorkers(await getIvrWorker());
        }
        catch (error) {
            console.error(WORKER_ERRORS.FETCH_WORKER_ERROR, error);
        }
        finally {
            setLoadingWorker(false);
        }
    };

    const fetchAssignment = async () => {
        try {
            setLoadingAssignment(true);
            setIvrAssignments(await getIvrAssignment());
        }
        catch (error) {
            console.error(ASSIGNMENT_ERRORS.FETCH_ASSIGNMENT_ERROR, error);
        }
        finally {
            setLoadingAssignment(false);
        }
    };

    const loadUsers = async () => {
        try {
            setLoadingUsers(true);
            setUsers(await getUsers());
        }
        catch (error) {
            console.error(USER_ERRORS.FETCH_USERS_ERROR, error);
        }
        finally {
            setLoadingUsers(false);
        }
    };
    
    const fetchReasons = async () => {
        try {
            setLoadingReasons(true);
            setUserReasons(await getActiveReasons());
        } catch (error) {
            console.error(STATUS_ERRORS.FETCH_ACTIVE_REASONS_ERROR, error);
        } finally {
            setLoadingReasons(false);
        }
    };

    const handleDisableWorkerClick = (worker = {}) => {
        setSelectedWorker(worker);
        setIsDisableWorkerModalOpen(true);
    };

    const handleDeleteWorkerClick = (worker = {}) => {
        setSelectedWorker(worker);
        setIsDeleteWorkerModalOpen(true);
    };

    const handleDeleteWorker = async (worker) => {
        setIsDeleting(true);
        setIsDeleteWorkerModalOpen(false);
        try {
            if (worker.assignedJobId) {
                const assignment = ivrAssignments.find(
                    (a) => a.jobid === worker.assignedJobId && a.workerId === worker.id
                );
                if (assignment) {
                    await completeIvrAssignment({
                        assignmentId: assignment.id,
                        workerId: assignment.workerId,
                        jobId: assignment.jobid,
                    });
                    await closeIvrAssignment({
                        assignmentId: assignment.id,
                        workerId: assignment.workerId,
                        jobId: assignment.jobid,
                    });
                }
            }
            await deleteIvrWorker(worker.id);
            setSuccessMessage(`${worker.id} deleted successfully`);
            await fetchWorker();
            await fetchAssignment();
        } catch (error) {
            console.error("Error deleting worker", error);
            setSuccessMessage(`Failed to delete worker ${worker.id}`);
        } finally {
            setIsDeleting(false);
            setTimeout(() => setSuccessMessage(""), 3000);
        }
    };

    const handleDisableWorker = async (worker) => {
        setIsDisabling(true);
        setIsDisableWorkerModalOpen(false);
        try {
            if (worker.assignedJobId) {
                const assignment = ivrAssignments.find(
                    (a) => a.jobid === worker.assignedJobId && a.workerId === worker.id
                );
                if (assignment) {
                    await completeIvrAssignment({
                        assignmentId: assignment.id,
                        workerId: assignment.workerId,
                        jobId: assignment.jobid,
                    });
                    await closeIvrAssignment({
                        assignmentId: assignment.id,
                        workerId: assignment.workerId,
                        jobId: assignment.jobid,
                    });
                }
            }
            await disableIvrWorker({
                workerId: worker.id,
                channelId: worker.channelId,
            });
            const agent = users.find(
                (user) => user.workerid === worker.id
            );
            if (agent) {
                const reasonId = userReasons.find(userReason => userReason.statereason === disableReason)?.id;
                await handleOfflineStatus(agent.id, reasonId);
            }
            setSuccessMessage(`${worker.id} disabled successfully`);
            await fetchWorker();
            await fetchAssignment();
            triggerStatusRefresh();
        } catch (error) {
            console.error("Error disabling worker", error);
            setSuccessMessage(`Failed to disable worker ${worker.id}`);
        } finally {
            setIsDisabling(false);
            setTimeout(() => setSuccessMessage(""), 3000);
        }
    };

    const { workersToDisplay, filteredWorkerLength } = useMemo(() => {
        const filteredWorker = ivrWorkers.filter((ivrWorker) =>
            ivrWorker.id.trim().toLowerCase().includes(searchIvrWorker.toLowerCase())
        );
        return {
            workersToDisplay: filteredWorker
                .sort(getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
            filteredWorkerLength: filteredWorker.length,
        };
    }, [ivrWorkers, searchIvrWorker, order, orderBy, page, rowsPerPage]);

    const handleRequestSort = (property) => {
        handleSort(order, orderBy, property, setOrder, setOrderBy);
    };

    return (
        <>
            {(isDeleting || isDisabling) && (
                <ProgressOverlay />
            )}
            {successMessage && 
                <Alert severity={STATUS.SUCCESS} sx={{ mb: 2, justifyContent: "center" }}>
                    {successMessage}
                </Alert>
            }
            <TextField
                fullWidth
                size={SIZES.SMALL}
                value={searchIvrWorker}
                variant={VARIANTS.OUTLINED}
                label={WORKER_STRINGS.SEARCH_WORKER}
                autoComplete={AUTO_COMPLETE.OFF}
                onChange={e => setSearchIvrWorker(e.target.value)}
            />
            <WorkerTable
                page={page}
                order={order}
                setPage={setPage}
                orderBy={orderBy}
                workers={workersToDisplay}
                rowsPerPage={rowsPerPage}
                columns={WORKER_TABLE_COLUMNS}
                setRowsPerPage={setRowsPerPage}
                onEdit={handleDisableWorkerClick}
                onDelete={handleDeleteWorkerClick}
                handleRequestSort={handleRequestSort}
                filteredWorkerLength={filteredWorkerLength}
                isWorkerLoading={loadingWorker || loadingAssignment || loadingUsers || loadingReasons}
            />
            <DeleteWorkerDialog
                open={isDeleteWorkerModalOpen}
                selectedWorker={selectedWorker}
                onConfirmDelete={handleDeleteWorker}
                onClose={() => setIsDeleteWorkerModalOpen(false)}
            />
            <DisableWorkerDialog
                open={isDisableWorkerModalOpen}
                selectedWorker={selectedWorker}
                onConfirmDisable={handleDisableWorker}
                onClose={() => setIsDisableWorkerModalOpen(false)}
            />
        </>
    );
};

export default Worker;